import React, { Component } from "react";
import PropTypes from "prop-types";
import "./Brush.css";
import { select } from "d3-selection";
import { scaleTime, scaleLinear } from "d3-scale";
import { brushX } from "d3-brush";
import { axisBottom } from "d3-axis";
import { bin, extent, max } from "d3-array";
import { timeMonth } from "d3-time";

class Brush extends Component {
  constructor(props) {
    super(props);
    this.createBrush = this.createBrush.bind(this);
  }

  componentDidMount() {
    this.createBrush();
    this.createMiniPlot();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.width !== this.props.width) {
      // redraw chart if window is resized
      this.clearComponent();
      this.createMiniPlot();
    }
    if (this.props.brushDidInvalidate) {
      this.clearComponent();
      this.createMiniPlot();
    }
    this.createBrush();
  }

  clearComponent() {
    const { node } = this;
    select(node)
      .selectAll("g")
      .remove();
  }

  createMiniPlot() {
    const { node } = this;
    const brushWidth = this.props.width - this.props.margin * 2;
    const scale = scaleTime()
      .domain(extent(this.props.data, d => d.time))
      .range([0, brushWidth]);

    const histogram = bin()
      .value(d => d.time)
      .domain(scale.domain())
      .thresholds(scale.ticks(timeMonth));

    const bins = histogram(this.props.data);
    const yHistScale = scaleLinear().range([25, 0]);

    yHistScale.domain([0, max(bins, d => d.length)]);

    select(node)
      .append("g")
      .attr("class", "brushplot")
      .attr("transform", `translate(${this.props.margin},0)`)
      .selectAll("rect")
      .data(bins)
      .enter()
      .append("rect")
      .attr("class", "minibar")
      .attr("transform", `translate(0,25)`)
      .attr("x", 1)
      .attr(
        "transform",
        d => `translate(${scale(d.x0)},${yHistScale(d.length)})`
      )
      .attr("width", d => scale(d.x1) - scale(d.x0) - 1)
      .attr("height", d => 25 - yHistScale(d.length))
      .style("stroke", "#F6F0ED")
      .style("fill", "#66adc9")
      .style("opacity", "0.7");
  }

  createBrush() {
    const { node } = this;
    const brushWidth = this.props.width - this.props.margin * 2;
    const scale = scaleTime()
      .domain(extent(this.props.data, d => d.time))
      .range([0, brushWidth]);

    const brushFn = this.props.setBrushExtent;

    function brushed(event) {
      const selectedExtent = event.selection.map(d => {
        return scale.invert(d);
      });
      const roundedExtent = selectedExtent.map(timeMonth.round);
      brushFn(roundedExtent);
    }

    const dayBrush = brushX()
      .extent([
        [0, 0],
        [brushWidth, this.props.margin]
      ])
      .on("brush", brushed);

    const dayAxis = axisBottom().scale(scale);

    select(node)
      .selectAll("g.brushaxis")
      .data([0])
      .enter()
      .append("g")
      .attr("class", "brushaxis")
      .attr("transform", `translate(${this.props.margin},25)`);

    select(node)
      .select("g.brushaxis")
      .call(dayAxis);

    select(node)
      .selectAll("g.brush")
      .data([0])
      .enter()
      .append("g")
      .attr("class", "brush")
      .attr("transform", `translate(${this.props.margin},0)`);

    select(node)
      .select("g.brush")
      .call(dayBrush);

    this.props.brushDidClear();
  }

  render() {
    return (
      <svg
        ref={node => (this.node = node)}
        width={this.props.width}
        height={50}
      />
    );
  }
}

const dataShape = PropTypes.shape({
  time: PropTypes.date,
  depth: PropTypes.number
});

Brush.propTypes = {
  width: PropTypes.number.isRequired,
  margin: PropTypes.number.isRequired,
  data: PropTypes.arrayOf(dataShape).isRequired,
  setBrushExtent: PropTypes.func.isRequired,
  brushDidClear: PropTypes.func.isRequired,
  brushDidInvalidate: PropTypes.bool.isRequired
};

export default Brush;
